/**
 * API service for the Work Item Detail Page.
 *
 * Exports thin wrappers around `apiRequest` for the `LoadWorkItem` and
 * `UpdateWorkItemState` BFF operations. Mirrors the pattern used by
 * `pages/workItems/api.ts`'s `fetchWorkItems` — single responsibility,
 * logs on failure, and re-throws so the caller can surface the canonical
 * `showError` title.
 *
 * Neither fetcher dispatches `showError` itself. The detail-page slice
 * (`workItemDetailSlice`) and the list-page `StartContinueTaskButton` own
 * those dispatches so a single implementation serves both call sites.
 *
 * Both fetchers also publish operational metrics (latency, count,
 * error-by-status) via `logOperationalMetric` so the client side can be
 * observed independently of the backend's own metrics — captures
 * client-perceived latency, network/CORS/timeout failures that never
 * reach the backend, and per-page funnel data.
 */

import { store } from "../../store";
import { selectStationCode } from "../../store/slices/shiftPlanSlice";
import { apiRequest } from "../../services/apiMiddleware";
import {
  API_GATEWAY_PROCESS_NAME,
  LOAD_WORK_ITEM_ENDPOINT,
  UPDATE_WORK_ITEM_STATE_ENDPOINT,
} from "../../utils/constants";
import {
  LOAD_WORK_ITEM_API,
  logOperationalMetric,
  MetricType,
  RCC_SHIFT_PLAN_WORK_ITEM_DETAIL_PAGE,
  UPDATE_WORK_ITEM_STATE_API,
} from "../../utils/metric-utils";
import {
  LoadWorkItemRequest,
  LoadWorkItemResponse,
  UpdateWorkItemStateRequest,
  UpdateWorkItemStateResponse,
} from "./model";

// Resolve the current station code at log time. The detail-page request
// bodies don't carry one, but operational metrics are still tagged with
// it so dashboards can split by station like the rest of the app. Defensive
// against tests that stub the store with an empty `getState()` — falls back
// to "UNKNOWN" rather than throwing inside a fetcher.
const stationCodeForMetrics = (): string => {
  try {
    return selectStationCode(store.getState() as any) ?? "UNKNOWN";
  } catch {
    return "UNKNOWN";
  }
};

// ---------------------------------------------------------------------------
// LoadWorkItem
// ---------------------------------------------------------------------------

export const fetchLoadWorkItem = async (
  request: LoadWorkItemRequest,
): Promise<LoadWorkItemResponse> => {
  const startTime = Date.now();
  const stationCode = stationCodeForMetrics();
  try {
    const response = await apiRequest<LoadWorkItemResponse>({
      resourcePath: LOAD_WORK_ITEM_ENDPOINT,
      httpMethod: "post",
      processName: API_GATEWAY_PROCESS_NAME,
      requestBody: request,
    });

    if (response.status && response.status >= 400) {
      throw new Error(response.errorResponse ?? "LoadWorkItem failed");
    }

    logOperationalMetric(
      startTime,
      stationCode,
      RCC_SHIFT_PLAN_WORK_ITEM_DETAIL_PAGE,
      LOAD_WORK_ITEM_API,
      MetricType.SUCCESS,
    );
    return response.data as LoadWorkItemResponse;
  } catch (error: any) {
    console.error("Error fetching LoadWorkItem:", error);
    logOperationalMetric(
      startTime,
      stationCode,
      RCC_SHIFT_PLAN_WORK_ITEM_DETAIL_PAGE,
      LOAD_WORK_ITEM_API,
      MetricType.FAILED,
      error,
    );
    throw error;
  }
};

// ---------------------------------------------------------------------------
// UpdateWorkItemState
// ---------------------------------------------------------------------------

export const fetchUpdateWorkItemState = async (
  request: UpdateWorkItemStateRequest,
): Promise<UpdateWorkItemStateResponse> => {
  const startTime = Date.now();
  const stationCode = stationCodeForMetrics();
  try {
    const response = await apiRequest<UpdateWorkItemStateResponse>({
      resourcePath: UPDATE_WORK_ITEM_STATE_ENDPOINT,
      httpMethod: "post",
      processName: API_GATEWAY_PROCESS_NAME,
      requestBody: request,
    });

    if (response.status && response.status >= 400) {
      throw new Error(response.errorResponse ?? "UpdateWorkItemState failed");
    }

    logOperationalMetric(
      startTime,
      stationCode,
      RCC_SHIFT_PLAN_WORK_ITEM_DETAIL_PAGE,
      UPDATE_WORK_ITEM_STATE_API,
      MetricType.SUCCESS,
    );
    return response.data as UpdateWorkItemStateResponse;
  } catch (error: any) {
    console.error("Error calling UpdateWorkItemState:", error);
    logOperationalMetric(
      startTime,
      stationCode,
      RCC_SHIFT_PLAN_WORK_ITEM_DETAIL_PAGE,
      UPDATE_WORK_ITEM_STATE_API,
      MetricType.FAILED,
      error,
    );
    throw error;
  }
};
